// 🚀 Worker-based Socket Transport Layer - Replaces direct socket.io calls

import { Logger } from '../../shared/services/logger.service';

export interface WorkerSocketConfig {
  host: string;
  namespace: string;
  path: string;
  auth: any;
}

export interface WorkerConnectionStatus {
  connected: boolean;
  socketId?: string;
  namespace: string;
  workerReady: boolean;
}

export type WorkerEventHandler = (envelope: any) => void;

/**
 * 🎯 TRANSPORT LAYER: Worker-based Socket.IO replacement
 *
 * This class provides the same interface as Socket.IO but uses Web Workers
 * to avoid browser tab throttling. Your existing SocketService business logic
 * remains unchanged - this just handles the transport.
 */
export class WorkerSocketService {
  private static instances: Map<string, WorkerSocketService> = new Map();
  private worker: Worker | null = null;
  private eventHandlers: Map<string, WorkerEventHandler> = new Map();
  private messageQueue: Array<any> = [];
  private isWorkerReady = false;
  private isConnected = false;
  private socketId: string | null = null;
  private namespace: string;
  private connectionPromise: Promise<void> | null = null;

  private constructor(namespace: string) {
    this.namespace = namespace;
    this.initializeWorker();
  }

  /**
   * Get or create worker instance for namespace (matches your Socket pattern)
   */
  static getInstance(namespace: string = '/events'): WorkerSocketService {
    if (!WorkerSocketService.instances.has(namespace)) {
      WorkerSocketService.instances.set(namespace, new WorkerSocketService(namespace));
    }
    return WorkerSocketService.instances.get(namespace)!;
  }

  private initializeWorker(): void {
    try {
      Logger.debug(`[WorkerTransport] 🚀 Initializing worker for ${this.namespace}`);

      this.worker = new Worker('/workers/socket.worker.js');
      this.setupWorkerListeners();
    } catch (error) {
      Logger.error('[WorkerTransport] ❌ Failed to initialize worker:', error);
      throw new Error(`Worker initialization failed: ${error.message}`);
    }
  }

  private setupWorkerListeners(): void {
    if (!this.worker) return;

    this.worker.onmessage = (e) => {
      const { type, data } = e.data;

      switch (type) {
        case 'WORKER_READY':
          this.isWorkerReady = true;
          Logger.debug('[WorkerTransport] ✅ Worker ready');
          break;

        case 'SOCKET_CONNECTED':
          this.isConnected = true;
          this.socketId = data.socketId;
          Logger.debug(`[WorkerTransport] ✅ Connected: ${data.socketId}`);
          this.flushMessageQueue();
          break;

        case 'SOCKET_DISCONNECTED':
          this.isConnected = false;
          this.socketId = null;
          Logger.debug('[WorkerTransport] ⚠️ Disconnected:', data.reason);
          break;

        case 'SOCKET_ERROR':
          Logger.error('[WorkerTransport] ❌ Socket error:', data.error);
          break;

        case 'SOCKET_EVENT_RECEIVED':
          // Forward to registered handlers (your existing SocketService)
          this.routeEvent(data._workerMetadata.originalEnvelope);
          break;

        case 'EVENT_EMITTED':
          Logger.debug('[WorkerTransport] 📤 Event emitted:', data.eventType);
          break;

        case 'EVENT_EMIT_ERROR':
          Logger.error('[WorkerTransport] ❌ Emit error:', data.error);
          break;
      }
    };

    this.worker.onerror = (error) => {
      Logger.error('[WorkerTransport] ❌ Worker error:', error);
    };
  }

  /**
   * 🔌 Initialize connection (replaces socket.io connection setup)
   */
  public async connect(config: WorkerSocketConfig): Promise<void> {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error('Worker not initialized'));
        return;
      }

      Logger.debug(`[WorkerTransport] 🔧 Connecting to ${config.host}${config.namespace}`);

      const handleConnectionResult = (e: MessageEvent) => {
        const { type, data } = e.data;

        if (type === 'SOCKET_CONNECTED') {
          this.worker?.removeEventListener('message', handleConnectionResult);
          resolve();
        } else if (type === 'SOCKET_ERROR') {
          this.worker?.removeEventListener('message', handleConnectionResult);
          reject(new Error(`Connection failed: ${data.error}`));
        }
      };

      this.worker.addEventListener('message', handleConnectionResult);

      this.worker.postMessage({
        type: 'INIT_SOCKET',
        data: config,
      });

      // Timeout fallback
      setTimeout(() => {
        this.worker?.removeEventListener('message', handleConnectionResult);
        reject(new Error('Connection timeout'));
      }, 10000);
    });

    return this.connectionPromise;
  }

  /**
   * 📤 Emit event (replaces socket.emit)
   */
  public async emit(eventName: string, envelope: any): Promise<void> {
    if (!this.worker) {
      throw new Error('Worker not initialized');
    }

    if (this.isConnected) {
      this.worker.postMessage({
        type: 'EMIT_EVENT',
        data: { envelope },
      });
    } else {
      // Queue for when connected (same as your existing logic)
      this.messageQueue.push({ eventName, envelope });
      console.debug(`[WorkerTransport] 📦 Queued event: ${envelope.event_type}`);
    }
  }

  /**
   * 📥 Register event listener (replaces socket.on)
   */
  public on(eventName: string, handler: WorkerEventHandler): void {
    this.eventHandlers.set(eventName, handler);
    console.debug(`[WorkerTransport] 📝 Registered handler for: ${eventName}`);
  }

  /**
   * 🗑️ Remove event listener (replaces socket.off)
   */
  public off(eventName: string): void {
    this.eventHandlers.delete(eventName);
    console.debug(`[WorkerTransport] 🗑️ Removed handler for: ${eventName}`);
  }

  /**
   * ⚡ Wait for connection (replaces socket connection check)
   */
  public async waitForConnection(): Promise<void> {
    if (this.isConnected) return;

    return new Promise<void>((resolve) => {
      const checkConnection = () => {
        if (this.isConnected) {
          resolve();
        } else {
          setTimeout(checkConnection, 100);
        }
      };
      checkConnection();
    });
  }

  /**
   * 📊 Connection status (matches your existing interface)
   */
  public getConnectionStatus(): WorkerConnectionStatus {
    return {
      connected: this.isConnected,
      socketId: this.socketId,
      namespace: this.namespace,
      workerReady: this.isWorkerReady,
    };
  }

  /**
   * 🔀 Route events to handlers
   */
  private routeEvent(envelope: any): void {
    this.eventHandlers.forEach((handler, eventName) => {
      try {
        // Call handler with the original envelope format
        handler(envelope);
      } catch (error) {
        console.error(`[WorkerTransport] ❌ Handler error for ${eventName}:`, error);
      }
    });
  }

  /**
   * 🔄 Flush queued messages
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) return;

    Logger.debug(`[WorkerTransport] 🔄 Flushing ${this.messageQueue.length} queued messages`);

    while (this.messageQueue.length > 0) {
      const { eventName, envelope } = this.messageQueue.shift();
      this.emit(eventName, envelope);
    }
  }

  /**
   * 🛑 Disconnect (replaces socket.disconnect)
   */
  public disconnect(): void {
    Logger.debug(`[WorkerTransport] 🛑 Disconnecting ${this.namespace}`);

    if (this.worker) {
      this.worker.postMessage({ type: 'DISCONNECT' });
      this.worker.terminate();
      this.worker = null;
    }

    this.eventHandlers.clear();
    this.messageQueue = [];
    this.isWorkerReady = false;
    this.isConnected = false;
    this.socketId = null;
    this.connectionPromise = null;

    WorkerSocketService.instances.delete(this.namespace);
  }

  /**
   * 🧹 Remove all listeners (replaces socket.removeAllListeners)
   */
  public removeAllListeners(): void {
    this.eventHandlers.clear();
  }

  // 🎯 Properties that match Socket.IO interface for compatibility
  public get connected(): boolean {
    return this.isConnected;
  }

  public get disconnected(): boolean {
    return !this.isConnected;
  }

  public get id(): string | null {
    return this.socketId;
  }
}

/**
 * 🏭 Factory function to create worker "socket" that matches Socket.IO interface
 */
export function createWorkerSocket(host: string, namespace: string, options: any) {
  const workerService = WorkerSocketService.getInstance(namespace);

  // Create a Socket.IO-like interface
  const workerSocket = {
    // Core Socket.IO methods
    emit: (eventName: string, data: any) => workerService.emit(eventName, data),
    on: (eventName: string, handler: any) => workerService.on(eventName, handler),
    off: (eventName: string, handler?: any) => workerService.off(eventName),
    once: (eventName: string, handler: any) => {
      const onceHandler = (data: any) => {
        handler(data);
        workerService.off(eventName);
      };
      workerService.on(eventName, onceHandler);
    },
    removeAllListeners: () => workerService.removeAllListeners(),
    disconnect: () => workerService.disconnect(),

    // Socket.IO properties
    get connected() {
      return workerService.connected;
    },
    get disconnected() {
      return workerService.disconnected;
    },
    get id() {
      return workerService.id;
    },

    // Initialize connection
    _connect: (config: WorkerSocketConfig) => workerService.connect(config),
  };

  return workerSocket;
}
